import { useEffect, useMemo, useState } from "react";
import { Logo } from "../components/Logo";
import { Btn } from "../components/ui/Button";
import { Avatar, StatusDot, StorageBar, Tag } from "../components/ui/Common";
import { adminService } from "../services/api";
import { formatBytes, formatDate, percent } from "../utils/format";
import { useResponsive } from "../hooks/useResponsive";

const defaultForm = {
  label: "",
  cloudName: "",
  totalStorageGb: 25,
  apiKey: "",
  apiSecret: "",
  status: "active",
};

export function AdminDashboard({ onNav, user, onLogout }) {
  const { isMobile, isTablet } = useResponsive();
  const [page, setPage] = useState("overview");
  const [accounts, setAccounts] = useState([]);
  const [pool, setPool] = useState(null);
  const [users, setUsers] = useState([]);
  const [userInsights, setUserInsights] = useState(null);
  const [settings, setSettings] = useState({ defaultUserStorageGb: 5 });
  const [logs, setLogs] = useState([]);
  const [system, setSystem] = useState(null);
  const [systemLogs, setSystemLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMessage, setTestMessage] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [userModal, setUserModal] = useState(null);
  const [userForm, setUserForm] = useState({ status: "active", allocatedStorageGb: 5 });
  const [userSaving, setUserSaving] = useState(false);
  const [deleteUserState, setDeleteUserState] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkForm, setBulkForm] = useState({ status: "paused", allocatedStorageGb: "" });
  const [bulkSaving, setBulkSaving] = useState(false);

  const loadAdminData = async () => {
    setLoading(true);
    setError("");
    try {
      const [
        accountsResponse,
        poolResponse,
        usersResponse,
        insightsResponse,
        settingsResponse,
        logsResponse,
        systemResponse,
        systemLogsResponse,
      ] = await Promise.all([
        adminService.getStorageAccounts(),
        adminService.getStoragePoolStats(),
        adminService.listUsers(),
        adminService.getUserInsights(),
        adminService.getSettings(),
        adminService.getActivityLogs(),
        adminService.getSystemOverview(),
        adminService.getSystemLogs(),
      ]);

      setAccounts(accountsResponse.data.accounts || []);
      setPool(poolResponse.data);
      setUsers(usersResponse.data.users || []);
      setUserInsights(insightsResponse.data || null);
      setSettings(settingsResponse.data.settings || { defaultUserStorageGb: 5 });
      setLogs(logsResponse.data.logs || []);
      setSystem(systemResponse.data || null);
      setSystemLogs(systemLogsResponse.data.logs || []);
      setSelectedUserIds([]);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load admin dashboard.");
    } finally {
      setLoading(false);
    }
  };

  const refreshSystemData = async () => {
    try {
      const [systemResponse, logsResponse] = await Promise.all([
        adminService.getSystemOverview(),
        adminService.getSystemLogs(),
      ]);
      setSystem(systemResponse.data || null);
      setSystemLogs(logsResponse.data.logs || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to refresh system status.");
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  const startCreate = () => {
    setEditingAccount(null);
    setModalMode("create");
    setForm(defaultForm);
    setTestMessage("");
    setShowForm(true);
  };

  const openAccount = async (account) => {
    try {
      const response = await adminService.getStorageAccount(account.id);
      const fullAccount = response.data.account || account;
      setEditingAccount(fullAccount);
      setModalMode("view");
      setForm({
        label: fullAccount.label || "",
        cloudName: fullAccount.cloudName || "",
        totalStorageGb: fullAccount.totalStorageGb || 25,
        apiKey: fullAccount.apiKey || "",
        apiSecret: fullAccount.apiSecret || "",
        status: fullAccount.status || "active",
      });
      setTestMessage("");
      setShowForm(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load account credentials.");
    }
  };

  const handleSaveAccount = async () => {
    setSaving(true);
    try {
      if (editingAccount) {
        await adminService.updateStorageAccount(editingAccount.id, form);
      } else {
        await adminService.createStorageAccount(form);
      }
      setShowForm(false);
      setEditingAccount(null);
      setModalMode("create");
      await loadAdminData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save storage account.");
    } finally {
      setSaving(false);
    }
  };

  const handleTestAccount = async () => {
    setTesting(true);
    setTestMessage("");
    try {
      const response = await adminService.testStorageAccount(form);
      setTestMessage(response.data?.message || "Connection successful");
    } catch (err) {
      setTestMessage(err.response?.data?.message || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleToggleStatus = async (account) => {
    try {
      if (account.status === "active") {
        await adminService.disableStorageAccount(account.id);
      } else {
        await adminService.enableStorageAccount(account.id);
      }
      await loadAdminData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update account status.");
    }
  };

  const handleSettingsSave = async () => {
    setSettingsSaving(true);
    try {
      const response = await adminService.updateSettings({
        defaultUserStorageGb: Number(settings.defaultUserStorageGb || 5),
      });
      setSettings(response.data.settings || settings);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update default user storage.");
    } finally {
      setSettingsSaving(false);
    }
  };

  const openUserManager = (managedUser) => {
    setUserModal(managedUser);
    setUserForm({
      status: managedUser.status || "active",
      allocatedStorageGb: managedUser.allocatedStorageGb || 5,
    });
  };

  const handleSaveUser = async () => {
    if (!userModal) return;
    setUserSaving(true);
    try {
      await adminService.updateUser(userModal.id, {
        status: userForm.status,
        allocatedStorageGb: Number(userForm.allocatedStorageGb || 5),
      });
      setUserModal(null);
      await loadAdminData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update user.");
    } finally {
      setUserSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserState) return;
    setUserSaving(true);
    try {
      await adminService.deleteUser(deleteUserState.id);
      setDeleteUserState(null);
      setUserModal(null);
      await loadAdminData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete user.");
    } finally {
      setUserSaving(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((current) =>
      current.includes(userId) ? current.filter((item) => item !== userId) : [...current, userId]
    );
  };

  const toggleSelectAllUsers = () => {
    const selectableIds = users.filter((item) => item.role !== "admin").map((item) => item.id);
    if (selectableIds.length > 0 && selectableIds.every((id) => selectedUserIds.includes(id))) {
      setSelectedUserIds([]);
      return;
    }
    setSelectedUserIds(selectableIds);
  };

  const handleBulkUpdateUsers = async () => {
    if (!selectedUserIds.length) return;
    setBulkSaving(true);
    try {
      const payload = {
        userIds: selectedUserIds,
      };

      if (bulkForm.status) payload.status = bulkForm.status;
      if (bulkForm.allocatedStorageGb !== "") {
        payload.allocatedStorageGb = Number(bulkForm.allocatedStorageGb);
      }

      await adminService.bulkUpdateUsers(payload);
      await loadAdminData();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update selected users.");
    } finally {
      setBulkSaving(false);
    }
  };

  const metrics = useMemo(() => {
    if (!pool) return [];
    const liveUsers = Number(userInsights?.summary?.onlineUsers || 0);
    return [
      {
        label: "Total Pool",
        value: formatBytes(pool.totalStorageBytes),
        accent: "#60a5fa",
        sub: `${pool.accountCount || 0} storage accounts`,
      },
      {
        label: "Used",
        value: formatBytes(pool.usedStorageBytes),
        accent: "#f59e0b",
        sub: `${percent(pool.usedStorageBytes, pool.totalStorageBytes)}% utilization`,
      },
      {
        label: "Available",
        value: formatBytes(pool.availableStorageBytes),
        accent: "#34d399",
        sub: "free capacity across the pool",
      },
      {
        label: "Users",
        value: String(users.length),
        accent: "#c084fc",
        sub: `${liveUsers} online right now`,
      },
    ];
  }, [pool, users.length, userInsights]);

  const accountSummary = useMemo(() => {
    return accounts.reduce(
      (acc, account) => {
        if (account.status === "active") acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { active: 0, inactive: 0 }
    );
  }, [accounts]);

  const userSummaryCards = useMemo(() => {
    const summary = userInsights?.summary || {};
    return [
      {
        label: "Users Online",
        value: String(summary.onlineUsers || 0),
        accent: "#34d399",
        sub: "seen in the last 5 minutes",
      },
      {
        label: "API Keys Live",
        value: String(summary.apiEnabledUsers || 0),
        accent: "#60a5fa",
        sub: "users with active API access",
      },
      {
        label: "API Requests",
        value: String(summary.totalApiRequests || 0),
        accent: "#f59e0b",
        sub: `${summary.totalApiUploads || 0} uploads, ${summary.totalApiDownloads || 0} downloads`,
      },
      {
        label: "API Transfer",
        value: formatBytes((summary.totalApiBytesUploaded || 0) + (summary.totalApiBytesDownloaded || 0)),
        accent: "#c084fc",
        sub: `${formatBytes(summary.totalApiBytesUploaded || 0)} up / ${formatBytes(summary.totalApiBytesDownloaded || 0)} down`,
      },
    ];
  }, [userInsights]);

  const systemCards = useMemo(() => {
    if (!system) return [];
    return [
      {
        label: "API Health",
        value: system.service?.status || "unknown",
        sub: system.service?.environment || "environment unavailable",
        accent: healthColor(system.service?.status),
      },
      {
        label: "Firestore",
        value: system.dependencies?.firestore?.status || "unknown",
        sub: system.dependencies?.firestore?.message || "no health data",
        accent: healthColor(system.dependencies?.firestore?.status),
      },
      {
        label: "Realtime DB",
        value: system.dependencies?.realtime?.status || "unknown",
        sub: system.dependencies?.realtime?.message || "no health data",
        accent: healthColor(system.dependencies?.realtime?.status),
      },
      {
        label: "Storage Provider",
        value: system.dependencies?.storage?.status || "unknown",
        sub: system.dependencies?.storage?.accountLabel || system.dependencies?.storage?.message || "no health data",
        accent: healthColor(system.dependencies?.storage?.status),
      },
    ];
  }, [system]);

  const navItems = [
    { id: "overview", label: "Overview" },
    { id: "server", label: "Server Ops" },
    { id: "accounts", label: "STORAC" },
    { id: "users", label: "Users" },
    { id: "logs", label: "Activity Logs" },
  ];

  const pageTitle =
    page === "server"
      ? "Server Operations"
      : page === "accounts"
        ? "STORAC"
        : page === "users"
          ? "Users"
          : page === "logs"
            ? "Activity Logs"
            : "Admin Control Center";
  const metricColumns = isMobile ? "1fr" : isTablet ? "repeat(2, minmax(0,1fr))" : "repeat(4, minmax(0,1fr))";
  const overviewSplitColumns = isMobile ? "1fr" : "1.15fr 0.85fr";
  const splitColumns = isMobile ? "1fr" : "1.05fr 0.95fr";
  const shellPadding = isMobile ? 16 : isTablet ? 20 : 28;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#07111f",
        color: "#e2e8f0",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        fontFamily: "'Space Grotesk','Space Mono',system-ui",
      }}
    >
      <aside
        style={{
          width: isMobile ? "100%" : isTablet ? 228 : 260,
          background: "rgba(10,15,28,0.95)",
          borderRight: isMobile ? "none" : "1px solid rgba(255,255,255,0.06)",
          borderBottom: isMobile ? "1px solid rgba(255,255,255,0.06)" : "none",
          display: "flex",
          flexDirection: "column",
          padding: isMobile ? "16px 0 0" : "24px 0",
        }}
      >
        <div style={{ padding: "0 20px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <Logo onClick={() => onNav("landing")} />
        </div>
        <div style={{ padding: "18px 14px", display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={user?.name || user?.email || "Admin"} size={44} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {user?.name || "Administrator"}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {user?.email}
            </div>
          </div>
        </div>
        <nav style={{ flex: 1, padding: "0 10px", display: "flex", flexDirection: isMobile ? "row" : "column", gap: isMobile ? 8 : 0, overflowX: isMobile ? "auto" : "visible" }}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setPage(item.id)}
              style={{
                width: isMobile ? "auto" : "100%",
                display: "flex",
                alignItems: "center",
                padding: "10px 14px",
                marginBottom: 4,
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                background: page === item.id ? "rgba(59,130,246,0.14)" : "transparent",
                color: page === item.id ? "#93c5fd" : "#94a3b8",
                fontWeight: page === item.id ? 700 : 500,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: "12px 10px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <button
            onClick={onLogout}
            style={{
              width: "100%",
              padding: "10px 14px",
              borderRadius: 12,
              border: "none",
              background: "rgba(239,68,68,0.08)",
              color: "#fca5a5",
              cursor: "pointer",
              fontWeight: 600,
              textAlign: "left",
            }}
          >
            Sign Out
          </button>
        </div>
      </aside>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          style={{
            minHeight: 72,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 14 : 0,
            padding: isMobile ? "16px" : "0 28px",
            background: "rgba(7,17,31,0.85)",
            backdropFilter: "blur(16px)",
          }}
        >
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-0.04em" }}>{pageTitle}</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>
              {page === "server"
                ? "Live runtime visibility, dependency checks, request activity, and operational diagnostics."
                : "Manage HexVault capacity, users, and provider credentials."}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", width: isMobile ? "100%" : "auto" }}>
            <Btn size="sm" variant="ghost" onClick={page === "server" ? refreshSystemData : loadAdminData}>
              Refresh
            </Btn>
            {(page === "overview" || page === "accounts") && (
              <Btn size="sm" onClick={startCreate}>Add Storage Account</Btn>
            )}
          </div>
        </header>
        <main style={{ flex: 1, padding: shellPadding, overflowY: "auto" }}>
          {error && <Banner message={error} tone="error" />}
          {loading ? (
            <div style={{ display: "grid", gap: 16 }}>
              {[1, 2, 3].map((item) => (
                <div key={item} style={{ ...panelStyle, height: 120, opacity: 0.6 }} />
              ))}
            </div>
          ) : (
            <>
              {page === "overview" && (
                <div className="hexvault-fade-up">
                  <div style={{ display: "grid", gridTemplateColumns: metricColumns, gap: 16, marginBottom: 24 }}>
                    {metrics.map((metric) => (
                      <MetricCard key={metric.label} metric={metric} />
                    ))}
                  </div>
                  <div style={{ ...panelStyle, marginBottom: 24 }}>
                    <SectionHeader
                      title="Storage Pool Utilization"
                      subtitle="Real-time account capacity across all configured storage providers."
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                      <span style={{ fontWeight: 700 }}>Current usage</span>
                      <span style={{ color: "#64748b", fontSize: 13 }}>
                        {formatBytes(pool?.usedStorageBytes || 0)} / {formatBytes(pool?.totalStorageBytes || 0)}
                      </span>
                    </div>
                    <StorageBar pct={percent(pool?.usedStorageBytes || 0, pool?.totalStorageBytes || 1)} showLabel={false} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: overviewSplitColumns, gap: 24 }}>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Live User Monitor"
                        subtitle="Real traffic, API activity, and recently active accounts from the production data store."
                      />
                      <RealtimeUsersPanel insights={userInsights} />
                    </div>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="API Usage Snapshot"
                        subtitle="Current API load and the highest-usage users by request volume."
                      />
                      <ApiUsageSnapshot insights={userInsights} />
                    </div>
                  </div>
                </div>
              )}
              {page === "server" && (
                <div className="hexvault-fade-up">
                  <div style={{ display: "grid", gridTemplateColumns: metricColumns, gap: 16, marginBottom: 24 }}>
                    {systemCards.map((metric) => (
                      <MetricCard key={metric.label} metric={metric} />
                    ))}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: splitColumns, gap: 24, marginBottom: 24 }}>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Runtime Health"
                        subtitle="These values come from the live API process and dependency checks."
                      />
                      <KeyValueGrid
                        items={[
                          ["Service Status", system?.service?.status],
                          ["Environment", system?.service?.environment],
                          ["Region", system?.service?.region],
                          ["Server Time", formatDate(system?.service?.serverTime)],
                          ["Started", formatDate(system?.service?.startedAt)],
                          ["Uptime", formatDuration(system?.service?.uptimeSeconds)],
                          ["Node Version", system?.service?.nodeVersion],
                          ["Platform", `${system?.service?.platform || "-"} / ${system?.service?.architecture || "-"}`],
                          ["API URL", system?.service?.apiUrl],
                          ["Public API URL", system?.service?.publicApiBaseUrl],
                          ["Download Base", system?.service?.publicDownloadBaseUrl],
                          ["Storage Provider", system?.service?.storageProviderName],
                        ]}
                      />
                    </div>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Runtime Resources"
                        subtitle="Memory and live request pressure from the current process."
                      />
                      <KeyValueGrid
                        items={[
                          ["Active Requests", String(system?.runtime?.activeRequests ?? 0)],
                          ["Total Requests", String(system?.runtime?.totalRequests ?? 0)],
                          ["Error Responses", String(system?.runtime?.errorResponses ?? 0)],
                          ["Last Request", formatDate(system?.runtime?.lastRequestAt)],
                          ["RSS Memory", formatBytes(system?.runtime?.memory?.rssBytes || 0)],
                          ["Heap Used", formatBytes(system?.runtime?.memory?.heapUsedBytes || 0)],
                          ["Heap Total", formatBytes(system?.runtime?.memory?.heapTotalBytes || 0)],
                          ["External Memory", formatBytes(system?.runtime?.memory?.externalBytes || 0)],
                          ["Load Avg (1m)", formatLoadValue(system?.runtime?.loadAverage?.[0])],
                          ["Load Avg (5m)", formatLoadValue(system?.runtime?.loadAverage?.[1])],
                          ["Load Avg (15m)", formatLoadValue(system?.runtime?.loadAverage?.[2])],
                        ]}
                      />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: splitColumns, gap: 24 }}>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Recent Server Requests"
                        subtitle="Live request samples captured by the running API instance."
                      />
                      <RequestsTable requests={system?.runtime?.recentRequests || []} />
                    </div>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Server Logs"
                        subtitle="Persisted error and system events recorded by the backend."
                      />
                      <SystemLogsTable logs={systemLogs} />
                    </div>
                  </div>
                </div>
              )}
              {page === "accounts" && (
                <div className="hexvault-fade-up">
                  <div style={{ display: "grid", gap: 24 }}>
                    <div style={{ display: "grid", gridTemplateColumns: metricColumns, gap: 16 }}>
                      <MetricCard metric={{ label: "Pool Total", value: formatBytes(pool?.totalStorageBytes || 0), accent: "#60a5fa", sub: `${accounts.length} total accounts` }} />
                      <MetricCard metric={{ label: "Active", value: String(accountSummary.active), accent: "#22c55e", sub: "eligible for allocation" }} />
                      <MetricCard metric={{ label: "Inactive", value: String(accountSummary.inactive), accent: "#ef4444", sub: "disabled from uploads" }} />
                      <MetricCard metric={{ label: "Allocation", value: "Serial", accent: "#c084fc", sub: "ordered by account label" }} />
                    </div>
                    <div style={panelStyle}>
                    <SectionHeader
                      title="STORAC"
                      subtitle="Every account here affects live capacity, upload allocation order, and provider health."
                    />
                    <AccountsGrid
                      accounts={accounts}
                      onView={openAccount}
                      onToggleStatus={handleToggleStatus}
                    />
                  </div>
                  </div>
                </div>
              )}
              {page === "users" && (
                <div className="hexvault-fade-up">
                  <div style={{ display: "grid", gap: 24 }}>
                    <div style={{ display: "grid", gridTemplateColumns: metricColumns, gap: 16 }}>
                      {userSummaryCards.map((metric) => (
                        <MetricCard key={metric.label} metric={metric} />
                      ))}
                    </div>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Default User Storage"
                        subtitle="This limit is applied to new user accounts by default. Individual users can be overridden below."
                      />
                      <div style={{ display: "flex", gap: 12, alignItems: "end", maxWidth: 420 }}>
                        <Field label="Default Storage (GB)">
                          <input
                            type="number"
                            value={settings.defaultUserStorageGb}
                            onChange={(event) =>
                              setSettings((current) => ({
                                ...current,
                                defaultUserStorageGb: event.target.value,
                              }))
                            }
                            style={inputStyle}
                          />
                        </Field>
                        <Btn onClick={handleSettingsSave} disabled={settingsSaving}>
                          {settingsSaving ? "Saving..." : "Save"}
                        </Btn>
                      </div>
                    </div>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Bulk User Actions"
                        subtitle="Pause, ban, reactivate, or change storage limits for multiple users at once."
                      />
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(4, minmax(0,1fr))", gap: 12, alignItems: "end" }}>
                        <Field label="Selected Users">
                          <StaticField value={`${selectedUserIds.length} selected`} />
                        </Field>
                        <Field label="Status">
                          <select
                            value={bulkForm.status}
                            onChange={(event) => setBulkForm((current) => ({ ...current, status: event.target.value }))}
                            style={inputStyle}
                          >
                            <option value="active">active</option>
                            <option value="paused">paused</option>
                            <option value="banned">banned</option>
                          </select>
                        </Field>
                        <Field label="Storage Limit (GB)">
                          <input
                            type="number"
                            value={bulkForm.allocatedStorageGb}
                            onChange={(event) => setBulkForm((current) => ({ ...current, allocatedStorageGb: event.target.value }))}
                            style={inputStyle}
                            placeholder="leave blank to keep current"
                          />
                        </Field>
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                          <button onClick={toggleSelectAllUsers} style={miniButtonStyle}>
                            {selectedUserIds.length === users.filter((item) => item.role !== "admin").length ? "Clear Selection" : "Select All"}
                          </button>
                          <Btn onClick={handleBulkUpdateUsers} disabled={bulkSaving || !selectedUserIds.length}>
                            {bulkSaving ? "Applying..." : "Apply Bulk Action"}
                          </Btn>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: splitColumns, gap: 24 }}>
                      <div style={panelStyle}>
                        <SectionHeader
                          title="Live Users"
                          subtitle="Users recently seen by authenticated web or API traffic."
                        />
                        <RecentlyActiveUsers insights={userInsights} />
                      </div>
                      <div style={panelStyle}>
                        <SectionHeader
                          title="API Key Oversight"
                          subtitle="Current user API keys and the accounts generating the most traffic."
                        />
                        <ApiKeysPanel insights={userInsights} />
                      </div>
                    </div>
                    <div style={panelStyle}>
                      <SectionHeader
                        title="Users"
                        subtitle="Manage account status, per-user storage limits, and destructive user cleanup."
                      />
                      <UsersTable
                        users={users}
                        selectedUserIds={selectedUserIds}
                        onToggleUser={toggleUserSelection}
                        onToggleAll={toggleSelectAllUsers}
                        onManage={openUserManager}
                        onDelete={(managedUser) => setDeleteUserState(managedUser)}
                      />
                    </div>
                  </div>
                </div>
              )}
              {page === "logs" && (
                <div className="hexvault-fade-up">
                  <div style={panelStyle}>
                    <SectionHeader
                      title="Activity Logs"
                      subtitle="Audit user, admin, and storage events synced by the application."
                    />
                    <LogsTable logs={logs} />
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
      {showForm && (
        <AccountModal
          form={form}
          setForm={setForm}
          onClose={() => setShowForm(false)}
          onSave={handleSaveAccount}
          onTest={handleTestAccount}
          onStartEdit={() => setModalMode("edit")}
          saving={saving}
          testing={testing}
          testMessage={testMessage}
          editing={Boolean(editingAccount)}
          mode={modalMode}
        />
      )}
      {userModal && (
        <UserManageModal
          user={userModal}
          form={userForm}
          setForm={setUserForm}
          saving={userSaving}
          onClose={() => setUserModal(null)}
          onSave={handleSaveUser}
          onDelete={() => setDeleteUserState(userModal)}
        />
      )}
      {deleteUserState && (
        <DeleteUserModal
          user={deleteUserState}
          busy={userSaving}
          onCancel={() => setDeleteUserState(null)}
          onConfirm={handleDeleteUser}
        />
      )}
    </div>
  );
}

function MetricCard({ metric }) {
  return (
    <div style={panelStyle}>
      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, letterSpacing: "0.08em", marginBottom: 10 }}>
        {metric.label.toUpperCase()}
      </div>
      <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: "-0.04em", color: metric.accent }}>
        {String(metric.value || "-").toUpperCase()}
      </div>
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 6, lineHeight: 1.6 }}>{metric.sub}</div>
    </div>
  );
}

function KeyValueGrid({ items }) {
  const { isMobile } = useResponsive();
  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0,1fr))", gap: 14 }}>
      {items.map(([label, value]) => (
        <div
          key={label}
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "14px 16px",
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
            {label}
          </div>
          <div title={String(value || "-")} style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700, wordBreak: "break-word", lineHeight: 1.6 }}>
            {value || "-"}
          </div>
        </div>
      ))}
    </div>
  );
}

function RequestsTable({ requests }) {
  if (!requests.length) {
    return <EmptyState title="No request samples yet" subtitle="Request traffic will appear here after the API handles live calls." />;
  }

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.08em" }}>
          {["Time", "Method", "Path", "Status", "Duration", "IP"].map((head) => (
            <th key={head} style={{ textAlign: "left", padding: "0 8px 12px", fontWeight: 600 }}>{head.toUpperCase()}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {requests.map((request) => (
          <tr key={request.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatDate(request.createdAt)}</td>
            <td style={{ padding: "12px 8px" }}>
              <Tag color={request.method === "POST" ? "#34d399" : request.method === "GET" ? "#60a5fa" : "#f59e0b"}>
                {request.method}
              </Tag>
            </td>
            <td style={{ padding: "12px 8px", color: "#e2e8f0", maxWidth: 280 }}>
              <div title={request.path} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {request.path}
              </div>
            </td>
            <td style={{ padding: "12px 8px" }}>
              <Tag color={Number(request.statusCode || 0) >= 500 ? "#ef4444" : Number(request.statusCode || 0) >= 400 ? "#f59e0b" : "#22c55e"}>
                {request.statusCode}
              </Tag>
            </td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{request.durationMs} ms</td>
            <td style={{ padding: "12px 8px", color: "#64748b" }}>{request.ip || "-"}</td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

function SystemLogsTable({ logs }) {
  if (!logs.length) {
    return <EmptyState title="No server logs yet" subtitle="Persisted backend errors and system events will show up here." />;
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {logs.map((log) => (
        <div
          key={log.id}
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "14px 16px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", marginBottom: 6 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
              <Tag color={log.level === "error" ? "#ef4444" : log.level === "warning" ? "#f59e0b" : "#60a5fa"}>
                {log.level || "info"}
              </Tag>
              <div style={{ color: "#e2e8f0", fontWeight: 700, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {log.type || "system.event"}
              </div>
            </div>
            <div style={{ color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(log.createdAt)}</div>
          </div>
          <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.7, marginBottom: 6 }}>
            {log.message}
          </div>
          {Object.keys(log.metadata || {}).length > 0 && (
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 12,
                lineHeight: 1.7,
                color: "#94a3b8",
                background: "rgba(2,6,23,0.44)",
                border: "1px solid rgba(255,255,255,0.05)",
                borderRadius: 12,
                padding: "10px 12px",
                fontFamily: "'Space Mono', monospace",
              }}
            >
              {JSON.stringify(log.metadata, null, 2)}
            </pre>
          )}
        </div>
      ))}
    </div>
  );
}

function AccountsGrid({ accounts, onView, onToggleStatus }) {
  const { isMobile } = useResponsive();
  if (!accounts.length) {
    return <EmptyState title="No storage accounts yet" subtitle="Add the first provider account to start building your storage pool." />;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0,1fr))", gap: 16 }}>
      {accounts.map((account) => (
        <div
          key={account.id}
          onClick={() => onView(account)}
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 18,
            padding: 18,
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <StatusDot active={account.status === "active"} />
                <span style={{ fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {account.label}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {account.cloudName || "-"}
              </div>
            </div>
            <Tag color={account.status === "active" ? "#22c55e" : "#ef4444"}>{account.status}</Tag>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>
              <span>Utilization</span>
              <span>{formatBytes(account.usedStorageBytes || 0)} / {formatBytes(account.totalStorageBytes || 0)}</span>
            </div>
            <StorageBar pct={percent(account.usedStorageBytes || 0, account.totalStorageBytes || 1)} showLabel={false} />
          </div>
          <div style={{ display: "grid", gap: 4, color: "#64748b", fontSize: 12, marginBottom: 14 }}>
            <div>Cloud name: {account.cloudName || "-"}</div>
            <div>Provider: Cloudinary</div>
            <div>Credentials: {account.credentialsConfigured ? "configured" : "missing"}</div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={(event) => { event.stopPropagation(); onView(account); }} style={miniButtonStyle}>View</button>
            <button
              onClick={(event) => { event.stopPropagation(); onToggleStatus(account); }}
              style={{
                ...miniButtonStyle,
                color: account.status === "active" ? "#fca5a5" : "#86efac",
              }}
            >
              {account.status === "active" ? "Disable" : "Enable"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function RealtimeUsersPanel({ insights }) {
  const users = insights?.recentlyActiveUsers || [];
  if (!users.length) {
    return <EmptyState title="No recent activity" subtitle="Users will appear here after authenticated web or API requests." />;
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {users.map((item) => (
        <div
          key={item.id}
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "14px 16px",
            display: "grid",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div style={{ fontWeight: 700 }}>{item.name || item.email || item.id}</div>
            <Tag color={item.onlineNow ? "#22c55e" : "#64748b"}>{item.onlineNow ? "online" : "idle"}</Tag>
          </div>
          <div style={{ color: "#94a3b8", fontSize: 12 }}>{item.email}</div>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", color: "#64748b", fontSize: 12 }}>
            <span>Last seen {formatDate(item.lastSeenAt)}</span>
            <span>{item.lastSeenSource || "web"}</span>
            <span>{item.lastSeenMethod || "-"}</span>
            <span>{item.lastSeenPath || "-"}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApiUsageSnapshot({ insights }) {
  const topApiUsers = insights?.topApiUsers || [];
  if (!topApiUsers.length) {
    return <EmptyState title="No API traffic yet" subtitle="API usage will appear here after users generate requests with active API keys." />;
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {topApiUsers.map((item) => (
        <div
          key={item.id}
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            padding: "14px 16px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 700 }}>{item.name || item.email || item.id}</div>
              <div style={{ color: "#64748b", fontSize: 12 }}>{item.email}</div>
            </div>
            <Tag color="#60a5fa">{item.apiAccessSummary?.enabled ? "api live" : "api off"}</Tag>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10, color: "#94a3b8", fontSize: 12 }}>
            <span>Total requests: {item.apiAccessSummary?.usage?.totalRequests || 0}</span>
            <span>Uploads: {item.apiAccessSummary?.usage?.uploadRequests || 0}</span>
            <span>Downloads: {item.apiAccessSummary?.usage?.downloadRequests || 0}</span>
            <span>Transfer: {formatBytes((item.apiAccessSummary?.usage?.bytesUploaded || 0) + (item.apiAccessSummary?.usage?.bytesDownloaded || 0))}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RecentlyActiveUsers({ insights }) {
  const users = insights?.recentlyActiveUsers || [];
  if (!users.length) {
    return <EmptyState title="No live users yet" subtitle="This panel updates once signed-in users interact with the site or API." />;
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {users.map((item) => (
        <div
          key={item.id}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "center",
            padding: "12px 14px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
          }}
        >
          <div>
            <div style={{ fontWeight: 700 }}>{item.name || item.email || item.id}</div>
            <div style={{ color: "#64748b", fontSize: 12 }}>{item.email}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <Tag color={item.onlineNow ? "#22c55e" : "#64748b"}>{item.onlineNow ? "online" : "recent"}</Tag>
            <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 6 }}>{formatDate(item.lastSeenAt)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ApiKeysPanel({ insights }) {
  const users = insights?.topApiUsers || [];
  if (!users.length) {
    return <EmptyState title="No API keys issued yet" subtitle="User API keys and usage totals will appear here once generated." />;
  }

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {users.map((item) => (
        <div
          key={item.id}
          style={{
            padding: "12px 14px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 16,
            display: "grid",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div style={{ fontWeight: 700 }}>{item.name || item.email || item.id}</div>
            <Tag color={item.apiAccessSummary?.enabled ? "#22c55e" : "#64748b"}>{item.apiAccessSummary?.enabled ? "enabled" : "disabled"}</Tag>
          </div>
          <div style={{ color: "#94a3b8", fontSize: 12 }}>{item.email}</div>
          <div style={{ color: "#cbd5e1", fontSize: 12, fontFamily: "'Space Mono', monospace", wordBreak: "break-all" }}>
            {item.apiAccessSummary?.lastGeneratedKey || `hv_live_****${item.apiAccessSummary?.last4 || "----"}`}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", color: "#64748b", fontSize: 12 }}>
            <span>Total: {item.apiAccessSummary?.usage?.totalRequests || 0}</span>
            <span>Last used: {formatDate(item.apiAccessSummary?.lastUsedAt)}</span>
            <span>{item.apiAccessSummary?.lastMethod || "-"} {item.apiAccessSummary?.lastEndpoint || "-"}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersTable({ users, selectedUserIds, onToggleUser, onToggleAll, onManage, onDelete }) {
  if (!users.length) {
    return <EmptyState title="No users yet" subtitle="User records will appear here after sign up and session sync." />;
  }

  const selectableUsers = users.filter((item) => item.role !== "admin");
  const allSelected = selectableUsers.length > 0 && selectableUsers.every((item) => selectedUserIds.includes(item.id));

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <table style={{ width: "100%", minWidth: 1520, borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.08em" }}>
          <th style={{ textAlign: "left", padding: "0 8px 12px", fontWeight: 600 }}>
            <input type="checkbox" checked={allSelected} onChange={onToggleAll} />
          </th>
          {["User", "Status", "Role", "Plan", "Allocated", "Used", "Online", "Last Seen", "API Key", "API Requests", "Upload Bytes", "Download Bytes", "Created", "Actions"].map((head) => (
            <th key={head} style={{ textAlign: "left", padding: "0 8px 12px", fontWeight: 600 }}>{head.toUpperCase()}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {users.map((item) => (
          <tr key={item.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <td style={{ padding: "12px 8px" }}>
              {item.role !== "admin" ? (
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(item.id)}
                  onChange={() => onToggleUser(item.id)}
                />
              ) : null}
            </td>
            <td style={{ padding: "12px 8px" }}>
              <div style={{ fontWeight: 700 }}>{item.name}</div>
              <div style={{ color: "#64748b", fontSize: 12 }}>{item.email}</div>
            </td>
            <td style={{ padding: "12px 8px" }}>
              <Tag color={userStatusColor(item.status)}>{item.status || "active"}</Tag>
            </td>
            <td style={{ padding: "12px 8px" }}>
              <Tag color={item.role === "admin" ? "#c084fc" : "#60a5fa"}>{item.role}</Tag>
            </td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{item.plan || "free"}</td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatBytes(item.allocatedStorageBytes || 0)}</td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatBytes(item.usedStorageBytes || 0)}</td>
            <td style={{ padding: "12px 8px" }}>
              <Tag color={item.onlineNow ? "#22c55e" : "#64748b"}>{item.onlineNow ? "online" : "offline"}</Tag>
            </td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatDate(item.lastSeenAt)}</td>
            <td style={{ padding: "12px 8px", color: "#cbd5e1", fontFamily: "'Space Mono', monospace", maxWidth: 220 }}>
              <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.apiAccessSummary?.lastGeneratedKey || (item.apiAccessSummary?.enabled ? `hv_live_****${item.apiAccessSummary?.last4 || "----"}` : "not issued")}
              </div>
            </td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{item.apiAccessSummary?.usage?.totalRequests || 0}</td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatBytes(item.apiAccessSummary?.usage?.bytesUploaded || 0)}</td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatBytes(item.apiAccessSummary?.usage?.bytesDownloaded || 0)}</td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatDate(item.createdAt)}</td>
            <td style={{ padding: "12px 8px" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => onManage(item)} style={miniButtonStyle}>Manage</button>
                {item.role !== "admin" && (
                  <button
                    onClick={() => onDelete(item)}
                    style={{ ...miniButtonStyle, color: "#fca5a5" }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

function UserManageModal({ user, form, setForm, saving, onClose, onSave, onDelete }) {
  const { isMobile } = useResponsive();
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2,6,23,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 110,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 720,
          background: "#0b1324",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 24,
          padding: 26,
          boxShadow: "0 30px 100px rgba(2,6,23,0.55)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>Manage User</div>
          <button onClick={onClose} style={closeButtonStyle}>Close</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,minmax(0,1fr))", gap: 14 }}>
          <Field label="Name"><StaticField value={user.name} /></Field>
          <Field label="Email"><StaticField value={user.email} /></Field>
          <Field label="Role"><StaticField value={user.role} /></Field>
          <Field label="Plan"><StaticField value={user.plan || "free"} /></Field>
          <Field label="Status">
            <select value={form.status} onChange={set("status")} style={inputStyle}>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="banned">banned</option>
            </select>
          </Field>
          <Field label="Storage Limit (GB)">
            <input type="number" value={form.allocatedStorageGb} onChange={set("allocatedStorageGb")} style={inputStyle} />
          </Field>
          <Field label="Used Storage"><StaticField value={formatBytes(user.usedStorageBytes || 0)} /></Field>
          <Field label="Created"><StaticField value={formatDate(user.createdAt)} /></Field>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "space-between", flexDirection: isMobile ? "column" : "row", marginTop: 20 }}>
          <div>
            {user.role !== "admin" && (
              <button onClick={onDelete} style={{ ...miniButtonStyle, color: "#fca5a5" }} disabled={saving}>
                Delete User
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, flexDirection: isMobile ? "column" : "row" }}>
            <button onClick={onClose} style={miniButtonStyle}>Cancel</button>
            <Btn onClick={onSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeleteUserModal({ user, busy, onCancel, onConfirm }) {
  const { isMobile } = useResponsive();
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2,6,23,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 120,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 560,
          background: "#0b1324",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 24,
          padding: 26,
          boxShadow: "0 30px 100px rgba(2,6,23,0.55)",
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 10 }}>Delete User?</div>
        <div style={{ color: "#94a3b8", lineHeight: 1.7, marginBottom: 18 }}>
          This will delete <strong style={{ color: "#e2e8f0" }}>{user.email}</strong> and remove all files owned by this user from HexVault.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexDirection: isMobile ? "column-reverse" : "row" }}>
          <button onClick={onCancel} style={miniButtonStyle}>Cancel</button>
          <Btn variant="danger" onClick={onConfirm} disabled={busy}>
            {busy ? "Deleting..." : "Delete User"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function LogsTable({ logs }) {
  if (!logs.length) {
    return <EmptyState title="No activity yet" subtitle="Admin and file activity will appear here." />;
  }

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.08em" }}>
          {["Time", "Type", "Actor", "Message"].map((head) => (
            <th key={head} style={{ textAlign: "left", padding: "0 8px 12px", fontWeight: 600 }}>{head.toUpperCase()}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {logs.map((log) => (
          <tr key={log.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{formatDate(log.createdAt)}</td>
            <td style={{ padding: "12px 8px" }}><Tag>{log.type}</Tag></td>
            <td style={{ padding: "12px 8px", color: "#94a3b8" }}>{log.actorEmail || "-"}</td>
            <td style={{ padding: "12px 8px", color: "#e2e8f0" }}>{log.message}</td>
          </tr>
        ))}
      </tbody>
      </table>
    </div>
  );
}

function AccountModal({
  form,
  setForm,
  onClose,
  onSave,
  onTest,
  onStartEdit,
  saving,
  testing,
  testMessage,
  editing,
  mode,
}) {
  const { isMobile } = useResponsive();
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const isViewMode = mode === "view";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2,6,23,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 100,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 720,
          background: "#0b1324",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 24,
          padding: 26,
          boxShadow: "0 30px 100px rgba(2,6,23,0.55)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>
            {isViewMode ? "Storage Account Details" : editing ? "Edit Storage Account" : "Add Storage Account"}
          </div>
          <button onClick={onClose} style={closeButtonStyle}>Close</button>
        </div>
        {isViewMode ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,minmax(0,1fr))", gap: 14 }}>
            <Field label="Account Label"><StaticField value={form.label} /></Field>
            <Field label="Cloud Name"><StaticField value={form.cloudName} /></Field>
            <Field label="Total Storage (GB)"><StaticField value={String(form.totalStorageGb)} /></Field>
            <Field label="Provider"><StaticField value="Cloudinary" /></Field>
            <Field label="API Key"><StaticField value={form.apiKey} mono /></Field>
            <Field label="API Secret"><StaticField value={form.apiSecret} mono /></Field>
            <Field label="Status"><StaticField value={form.status} /></Field>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,minmax(0,1fr))", gap: 14 }}>
            <Field label="Account Label"><input value={form.label} onChange={set("label")} style={inputStyle} /></Field>
            <Field label="Cloud Name"><input value={form.cloudName} onChange={set("cloudName")} style={inputStyle} /></Field>
            <Field label="Total Storage (GB)"><input type="number" value={form.totalStorageGb} onChange={set("totalStorageGb")} style={inputStyle} /></Field>
            <Field label="API Key"><input value={form.apiKey} onChange={set("apiKey")} style={inputStyle} /></Field>
            <Field label="API Secret"><input value={form.apiSecret} onChange={set("apiSecret")} style={inputStyle} /></Field>
          </div>
        )}
        {testMessage && (
          <div
            style={{
              marginTop: 16,
              background: "rgba(59,130,246,0.1)",
              border: "1px solid rgba(96,165,250,0.25)",
              color: "#bfdbfe",
              borderRadius: 12,
              padding: "12px 14px",
            }}
          >
            {testMessage}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexDirection: isMobile ? "column-reverse" : "row", marginTop: 20 }}>
          <button onClick={onClose} style={miniButtonStyle}>Cancel</button>
          {isViewMode ? (
            <Btn onClick={onStartEdit}>Edit Account</Btn>
          ) : (
            <>
              <button onClick={onTest} style={miniButtonStyle} disabled={testing}>{testing ? "Testing..." : "Test Connection"}</button>
              <Btn onClick={onSave} disabled={saving}>{saving ? "Saving..." : editing ? "Save Changes" : "Create Account"}</Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StaticField({ value, mono = false }) {
  return (
    <div
      title={value || "-"}
      style={{
        ...inputStyle,
        minHeight: 46,
        display: "flex",
        alignItems: "center",
        color: "#e2e8f0",
        wordBreak: "break-all",
        lineHeight: 1.5,
        fontFamily: mono ? "'Space Mono', monospace" : "inherit",
      }}
    >
      {value || "-"}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function SectionHeader({ title, subtitle }) {
  const { isMobile } = useResponsive();
  return (
    <div style={{ marginBottom: 16, display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "end", justifyContent: "space-between", gap: 12 }}>
      <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>{title}</div>
      <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.7 }}>{subtitle}</div>
    </div>
  );
}

function Banner({ message, tone }) {
  const color = tone === "error" ? "#fca5a5" : "#86efac";
  const border = tone === "error" ? "rgba(239,68,68,0.3)" : "rgba(34,197,94,0.3)";
  const background = tone === "error" ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)";
  return (
    <div style={{ background, border: `1px solid ${border}`, color, borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
      {message}
    </div>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <div style={{ textAlign: "center", padding: "50px 20px" }}>
      <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div style={{ color: "#64748b" }}>{subtitle}</div>
    </div>
  );
}

function healthColor(status) {
  if (status === "healthy") return "#22c55e";
  if (status === "warning") return "#f59e0b";
  if (status === "degraded" || status === "error") return "#ef4444";
  return "#60a5fa";
}

function userStatusColor(status) {
  if (status === "active") return "#22c55e";
  if (status === "paused") return "#f59e0b";
  if (status === "banned") return "#ef4444";
  return "#60a5fa";
}

function formatDuration(totalSeconds = 0) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remaining = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${remaining}s`;
  if (minutes > 0) return `${minutes}m ${remaining}s`;
  return `${remaining}s`;
}

function formatLoadValue(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "-";
}

const panelStyle = {
  background: "rgba(10,15,28,0.76)",
  border: "1px solid rgba(148,163,184,0.12)",
  borderRadius: 22,
  padding: 22,
  boxShadow: "0 24px 80px rgba(2,6,23,0.3)",
};

const inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 12,
  padding: "12px 14px",
  color: "#e2e8f0",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const miniButtonStyle = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#cbd5e1",
  borderRadius: 8,
  padding: "6px 10px",
  cursor: "pointer",
  fontSize: 12,
};

const closeButtonStyle = {
  background: "none",
  border: "none",
  color: "#94a3b8",
  cursor: "pointer",
  fontSize: 14,
};
